import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useState, type MouseEvent } from 'react'
import { Button, Typography } from 'shared/ui'
import { ModalLargeIcon } from 'shared/ui/Icon/ModalLargeIcon/ModalLargeIcon'
import { type ModalType, type ModalTypeList } from 'shared/ui/Modal/store/types'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { getAriaLabel } from 'shared/lib'
import styles from './styles.module.scss'

export interface InfoModalContentProps {
  modal: ModalType & { type: ModalTypeList }
  hide?: boolean
}

export const InfoModalContent = observer(({ modal, hide }: InfoModalContentProps) => {
  const {
    id,
    primaryAction,
    secondaryAction,
    ModalContentProps,
    ModalActions,
    additionalSecondaryAction,
    type,
    title,
    subtitle,
    desc,
    showCloseButton = true,
    closeActionButtonProps,
  } = modal

  const [loadingPrimary, setLoadingPrimary] = useState(false)
  const [loadingSecondary, setLoadingSecondary] = useState(false)
  const [loadingAdditionalSecondary, setLoadingAdditionalSecondary] = useState(false)

  const onClose = () => {
    modalStore.closeModal(id)
  }

  const handlePrimary = async (e: MouseEvent<HTMLButtonElement>) => {
    if (primaryAction?.onAction) {
      try {
        setLoadingPrimary(true)
        await primaryAction?.onAction(e)
      } finally {
        setLoadingPrimary(false)
      }
    }
  }

  const handleSecondary = async (e: MouseEvent<HTMLButtonElement>) => {
    if (secondaryAction?.onAction) {
      try {
        setLoadingSecondary(true)
        await secondaryAction?.onAction(e)
      } finally {
        setLoadingSecondary(false)
      }
    }
  }

  const handleAdditionalSecondary = async (e: MouseEvent<HTMLButtonElement>) => {
    if (primaryAction?.onAction) {
      try {
        setLoadingAdditionalSecondary(true)
        await additionalSecondaryAction?.onAction(e)
      } finally {
        setLoadingAdditionalSecondary(false)
      }
    }
  }

  const ariaLabel = 'InfoModalContent'

  return (
    <div
      className={styles.wrap}
      aria-label={getAriaLabel(ariaLabel, 'wrap')}
      onClick={(event) => {
        if (modal.isStopPropagation) {
          event.stopPropagation()
        }
      }}
    >
      <div
        id={`modal-card-${id}`}
        className={classNames(styles.card, styles.info)}
        style={{ visibility: hide ? 'hidden' : 'unset' }}
        aria-label={getAriaLabel(ariaLabel, 'card')}
      >
        <ModalLargeIcon type={type} />
        <div className={styles.content} aria-label={getAriaLabel(ariaLabel, 'content')}>
          {!!title && (
            <Typography
              variant={'heading-md'}
              className={classNames({ [styles.mb12]: !!subtitle || !!desc })}
              ariaLabel={'title'}
            >
              {title}
            </Typography>
          )}
          {!!subtitle && (
            <Typography
              variant={'body-lg-regular'}
              className={classNames({ [styles.mb12]: !!desc })}
              ariaLabel={'subtitle'}
            >
              {subtitle}
            </Typography>
          )}
          {!!desc && (
            <Typography
              variant={'body-rg-regular'}
              className={styles.textCenter}
              ariaLabel={'desc'}
              color='var(--content-primary-tertiary)'
            >
              {desc}
            </Typography>
          )}
        </div>
        <div className={styles.actions} aria-label={getAriaLabel(ariaLabel, 'actions')}>
          {ModalActions ? (
            <ModalActions {...ModalContentProps} />
          ) : (
            <>
              {primaryAction && (
                <Button
                  typeBtn={'contained'}
                  className={styles.fullWidth}
                  text={primaryAction.text}
                  disabled={Boolean(primaryAction.disabled)}
                  onClick={handlePrimary}
                  contained={'primary'}
                  loading={loadingPrimary}
                />
              )}
              {additionalSecondaryAction && (
                <Button
                  typeBtn={'contained'}
                  className={classNames(styles.fullWidth, styles.additionalSecondaryAction)}
                  text={additionalSecondaryAction.text}
                  disabled={Boolean(additionalSecondaryAction.disabled)}
                  onClick={handleAdditionalSecondary}
                  contained={'secondary'}
                  loading={loadingAdditionalSecondary}
                />
              )}
              {secondaryAction && (
                <Button
                  typeBtn={'contained'}
                  className={styles.fullWidth}
                  text={secondaryAction.text}
                  disabled={Boolean(secondaryAction.disabled)}
                  onClick={handleSecondary}
                  contained={'secondary'}
                  loading={loadingSecondary}
                />
              )}
              {!secondaryAction && showCloseButton && (
                <Button
                  typeBtn={'contained'}
                  className={styles.fullWidth}
                  text={modal.infoModalTextAction || 'Close'}
                  onClick={onClose}
                  disabled={modalStore.disabledRemoveIds.has(modal.id)}
                  {...closeActionButtonProps}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
})
